import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { SearchResult } from '../../../../../../common/ui/search-result';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import { LoomVideoIcon } from '../common/loom-video-icon';
import { convertVideoDuration } from '../search-graph-result/search-graph-video/convert-video-duration';
import { messages } from '../search-graph-result/search-graph-video/messages';
import type { searchLoomVideo_AtlassianSearchPage$key } from './__generated__/searchLoomVideo_AtlassianSearchPage.graphql';

export const isSearchLoomVideo = (node: { __typename?: string }): boolean =>
	node.__typename === 'SearchResultLoomVideo';

export const SearchLoomVideo = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	appMode,
	selectedProduct,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchResultProps<searchLoomVideo_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchLoomVideo_AtlassianSearchPage on SearchResultLoomVideo {
				id
				title
				description
				type
				url
				durationSeconds
				creatorName
			}
		`,
		node,
	);
	const { formatMessage } = useIntl();
	const [, , , contentId] = useMemo(() => getProductAndIDFromARI(data.id), [data.id]);
	const metadata = useMemo(
		() =>
			({
				id: data.id,
				containerId: null,
				spaceId: null,
				contentType: data.type,
				entityType: data.type,
				contentId,
				product: ProductKeys.Loom,
				...getSiteMetadata(data.id, cloudId, selectedSites),
			}) satisfies AdvancedSearchResultInteractedMetadata,
		[cloudId, contentId, data.id, data.type, selectedSites],
	);

	const duration = convertVideoDuration(data.durationSeconds, {
		readable: true,
		formatMessage,
		largestUnitOnly: true,
	});
	const description = data.description ? (
		<DescriptionBody description={processExcerpt(data.description)} />
	) : null;

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{(callbacks) => (
				<SearchResult
					productConfigs={productConfigs}
					{...callbacks}
					id={data.id}
					product={ProductKeys.Loom}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					url={data.url}
					title={enrichExcerpt(data.title)}
					icon={<LoomVideoIcon />}
					headerMetadataParts={[
						formatMessage(messages.videoType),
						data.creatorName && fg('loom-rovo-search') ? ['creator-name', data.creatorName] : null,
						['duration', duration],
					]}
					description={description}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};
